import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';

// Chip + dropdown multi-select. Lifted out of pages/Users.jsx so the new-user
// defaults modal can use the same control — importing it back from the page
// would have made Users.jsx and that modal circular.
export const MultiSelectDropdown = ({ options, selectedCodes, onChange, placeholder, aispaceColor,
                                     allOptionLabel = null }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const toggle = (code) => {
    if (selectedCodes.includes(code)) {
      onChange(selectedCodes.filter((c) => c !== code));
    } else {
      onChange([...selectedCodes, code]);
    }
  };

  const removeChip = (code, e) => {
    e.stopPropagation();
    onChange(selectedCodes.filter((c) => c !== code));
  };

  const selected = options.filter((o) => selectedCodes.includes(o.code));
  // Reflects the current option list, so adding a group later un-ticks "All"
  // rather than silently claiming everything is still selected.
  const allSelected = options.length > 0 && selected.length === options.length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[46px] flex items-center justify-between gap-2 border rounded-lg px-3 py-2 text-[14px] bg-white hover:bg-[#F9FAFB] transition-colors focus:outline-none"
        style={open ? { borderColor: aispaceColor } : { borderColor: '#E5E7EB' }}
      >
        <div className="flex flex-wrap gap-1.5 flex-1 text-left">
          {selected.length === 0 ? (
            <span className="text-[#9CA3AF] py-1">{placeholder}</span>
          ) : (
            selected.map((opt) => (
              <span
                key={opt.code}
                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 text-[13px] text-[#374151] bg-white border border-[#E5E7EB] rounded-full"
              >
                {opt.label}
                <button
                  type="button"
                  onClick={(e) => removeChip(opt.code, e)}
                  className="p-0.5 hover:bg-[#F3F4F6] rounded-full"
                >
                  <X size={12} className="text-[#6B7280]" />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDown size={16} className={`text-[#6B7280] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-30 max-h-[260px] overflow-y-auto py-1">
          {options.length === 0 ? (
            <div className="px-4 py-3 text-[13px] text-[#9CA3AF]">{t('users.filterNoOptions')}</div>
          ) : (
            <>
              {allOptionLabel && (
                <button
                  type="button"
                  onClick={() => onChange(allSelected ? [] : options.map((o) => o.code))}
                  className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] font-medium text-[#111827] border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors ${allSelected ? 'bg-[#F3F4F6]' : ''}`}
                >
                  <span className="truncate">{allOptionLabel}</span>
                  {allSelected && <Check size={16} style={{ color: aispaceColor }} />}
                </button>
              )}
              {options.map((opt) => {
              const isSel = selectedCodes.includes(opt.code);
              return (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => toggle(opt.code)}
                  className={`w-full px-4 py-2.5 flex items-center justify-between text-left text-[14px] text-[#111827] hover:bg-[#F9FAFB] transition-colors ${isSel ? 'bg-[#F3F4F6]' : ''}`}
                >
                  <span className="truncate">{opt.label}</span>
                  {isSel && <Check size={16} style={{ color: aispaceColor }} />}
                </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
